import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  notification,
  Input,
  Pagination,
  Button,
  Segmented,
  Grid,
  DatePicker,
  Select,
  Checkbox,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  FileProtectOutlined,
  AppstoreOutlined,
  TableOutlined,
} from "@ant-design/icons";
import lib, { myRequestObj } from "../../assets/utils/lib";
import DebounceSelect from "../compoinents/DebouncedSelect";
import { parseSortParams, toSortQuery, SORT_BY, SORT_DIR } from "../../utils/tableSortParams";
import dayjs from "dayjs";
import "./preauth.css";
import "./preauth-queue-dashboard.css";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import useLocalStorage from "../../assets/utils/useLocal";
import DashboardSection from "../dashboard/components/DashboardSection";
import { TableSkeleton } from "../dashboard/components/DashboardSkeleton";
import { SearchResultsSkeleton } from "../search/SearchResultCards";
import { PAClaimListingCard } from "./PAClaimListingCard";
import PAQueueTableDefault, { PAQueueTable as PAQueueTableNamed } from "./PAQueueTable";
import { CHECKIN_TYPE_MAP, withResolvedCheckinType } from "./checkinTypeLabel";

const PAQueueTable = PAQueueTableNamed || PAQueueTableDefault;

const STATUS_CONFIG = {
  Pending: { color: "#F39C12", text: "Pending" },
  Approved: { color: "#49B870", text: "Approved" },
  Queried: { color: "#3497D3", text: "Queried" },
  Declined: { color: "#E74C3C", text: "Rejected" },
  Manual: { color: "#6C757D", text: "Manual" },
};

const EMPTY_CLAIMS = {
  Pending: { data: [], pagination: { current: 1, pageSize: 20, total: 0 } },
  Approved: { data: [], pagination: { current: 1, pageSize: 20, total: 0 } },
  Queried: { data: [], pagination: { current: 1, pageSize: 20, total: 0 } },
  Declined: { data: [], pagination: { current: 1, pageSize: 20, total: 0 } },
  Manual: { data: [], pagination: { current: 1, pageSize: 20, total: 0 } },
};

function ymdFromParam(value) {
  if (!value) return "";
  const slice = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(slice) ? slice : "";
}

function readPreauthQueueParams(search) {
  const params = new URLSearchParams(search || "");
  const sort = parseSortParams(params, { sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC });
  const num = (key) => {
    const value = params.get(key);
    if (value == null || value === "") return "";
    return value;
  };
  const typeRaw = params.get("type") || params.get("checkin_type_id") || params.get("checkin_type_ids") || "";
  return {
    facility_id: params.get("facility_id") || params.get("location_id") || "",
    fromDate: ymdFromParam(params.get("from") || params.get("fromDate") || params.get("date_from")),
    toDate: ymdFromParam(params.get("to") || params.get("toDate") || params.get("date_to")),
    checkin_type_id: typeRaw ? String(typeRaw).split(",")[0] : "",
    flagged: params.get("flagged") === "1" || params.get("flagged") === "true",
    sort_by: sort.sort_by,
    sort_dir: sort.sort_dir,
    items_min: num("items_min"),
    items_max: num("items_max"),
    requested_min: num("requested_min"),
    requested_max: num("requested_max"),
    approved_min: num("approved_min"),
    approved_max: num("approved_max"),
    s: params.get("s") || "",
  };
}

const PAListHook = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { canStayOnModule } = usePermissions();
  const [claimsData, setClaimsData] = useState(EMPTY_CLAIMS);
  const [activeSegment, setActiveSegment] = useState("Pending");
  const [filterKeyword, setFilterKeyword] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [viewMode, setViewMode] = useLocalStorage("preauth-view-mode-v2", "table");
  const hasInitialized = useRef(false);
  const [queueFilters, setQueueFilters] = useState(() => readPreauthQueueParams(location.search));
  const [facilityOption, setFacilityOption] = useState(null);
  const [checkinTypes, setCheckinTypes] = useState([]);

  const screens = Grid.useBreakpoint();
  const isMobileView = !screens.md;

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Page-stay check. canStayOnModule applies the legacy role fallback only when the
    // user's RBAC permission map is empty, so no role id appears in this file.
    if (!canStayOnModule("preauth", [1, 2, 3, 4])) {
      notification.warning({
        message: "Access Denied",
        description:
          "You don't have permission to access Pre-Authorization. Please contact an administrator.",
        placement: "topLeft",
      });
      navigate("/dashboard");
      return;
    }

    if (!hasInitialized.current) {
      const params = new URLSearchParams(location.search);
      const parsed = readPreauthQueueParams(location.search);
      const segmentFromUrl = params.get("s");
      const segmentFromStorage = localStorage.getItem("paActiveSegment");
      const initialSegment = segmentFromUrl || segmentFromStorage || "Pending";

      setQueueFilters(parsed);
      if (parsed.facility_id) {
        setFacilityOption({
          value: parsed.facility_id,
          label: `Facility #${parsed.facility_id}`,
        });
      }
      setActiveSegment(initialSegment);
      hasInitialized.current = true;
    }
  }, [user, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && hasInitialized.current) {
      fetchSegmentData(activeSegment, 1, filterKeyword);
    }
  }, [activeSegment, queueFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    lib.listAdminCheckinTypes({ status: "active" })
      .then((resp) => {
        const parsed = lib.parseApiEnvelope(resp);
        const payload = parsed.data ?? parsed.body;
        const rows = Array.isArray(payload)
          ? payload
          : payload?.items || payload?.types || payload?.checkin_types || [];
        if (cancelled) return;
        const options = (rows || [])
          .map((t) => ({
            value: String(t.checkin_type_id ?? t.id ?? ""),
            label: t.checkin_type_name ?? t.name ?? t.label,
          }))
          .filter((t) => t.value && t.label);
        if (options.length) setCheckinTypes(options);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const writeQueueParams = useCallback(
    (patch, { resetPage = true } = {}) => {
      const next = { ...queueFilters, ...patch };
      setQueueFilters(next);
      const params = new URLSearchParams(location.search);
      const pairs = {
        s: patch.s || activeSegment,
        facility_id: next.facility_id,
        from: next.fromDate,
        to: next.toDate,
        type: next.checkin_type_id,
        flagged: next.flagged ? "1" : "",
        sort_by: next.sort_by && next.sort_by !== SORT_BY.CHECKIN_DATE ? next.sort_by : "",
        sort_dir: next.sort_dir && next.sort_dir !== SORT_DIR.DESC ? next.sort_dir : "",
        items_min: next.items_min,
        items_max: next.items_max,
        requested_min: next.requested_min,
        requested_max: next.requested_max,
        approved_min: next.approved_min,
        approved_max: next.approved_max,
      };
      Object.entries(pairs).forEach(([key, value]) => {
        if (value == null || value === "" || value === false) params.delete(key);
        else params.set(key, String(value));
      });
      params.delete("location_id");
      params.delete("fromDate");
      params.delete("toDate");
      params.delete("checkin_type_id");
      params.delete("date_from");
      params.delete("date_to");
      navigate(`?${params.toString()}`, { replace: true });
      if (resetPage) {
        setClaimsData((prev) => ({
          ...prev,
          [activeSegment]: {
            ...prev[activeSegment],
            pagination: { ...prev[activeSegment].pagination, current: 1 },
          },
        }));
      }
    },
    [queueFilters, location.search, navigate, activeSegment]
  );

  const fetchSegmentData = async (status, page = 1, search = "") => {
    setLoading(true);
    try {
      const payload = {
        status,
        page,
        pageSize: 20,
        search,
        ...toSortQuery(queueFilters.sort_by, queueFilters.sort_dir),
      };
      if (queueFilters.facility_id) payload.facility_id = queueFilters.facility_id;
      if (queueFilters.fromDate) payload.fromDate = queueFilters.fromDate;
      if (queueFilters.toDate) payload.toDate = queueFilters.toDate;
      if (queueFilters.checkin_type_id) payload.checkin_type_id = queueFilters.checkin_type_id;
      if (queueFilters.flagged) payload.flagged = 1;
      if (queueFilters.items_min !== "" && queueFilters.items_min != null) payload.items_min = queueFilters.items_min;
      if (queueFilters.items_max !== "" && queueFilters.items_max != null) payload.items_max = queueFilters.items_max;
      if (queueFilters.requested_min !== "" && queueFilters.requested_min != null) payload.requested_min = queueFilters.requested_min;
      if (queueFilters.requested_max !== "" && queueFilters.requested_max != null) payload.requested_max = queueFilters.requested_max;
      if (queueFilters.approved_min !== "" && queueFilters.approved_min != null) payload.approved_min = queueFilters.approved_min;
      if (queueFilters.approved_max !== "" && queueFilters.approved_max != null) payload.approved_max = queueFilters.approved_max;

      const response = await myRequestObj(`PAListAdminNew`).post(payload);

      let data = await response?.data;
      if (!data || Number(data?.error) !== 0) {
        notification.error({
          message: "Error",
          description: data?.message || "Failed to fetch authorization requests",
        });
        return;
      }

      data = data?.data;

      const claims = (data?.claims || []).map(withResolvedCheckinType);

      setClaimsData((prev) => ({
        ...prev,
        [status]: {
          data: claims,
          pagination: {
            ...prev[status].pagination,
            current: page,
            total: data?.totalCount,
          },
        },
      }));

      const checkinIds = claims
        .map((c) => c?.code || c?.checkin_id || c?.id)
        .filter(Boolean);

      if (checkinIds.length > 0) {
        lib
          .getFlagAggregates({
            target_type: "checkin",
            target_ids: checkinIds,
          })
          .then((aggResp) => {
            const aggData = aggResp?.data?.data || {};
            const flagsMap = Object.entries(aggData).reduce((acc, [key, val]) => {
              acc[key] = {
                total_flags: Number(val?.active_count) || 0,
                has_active_flags: (Number(val?.active_count) || 0) > 0,
                highest_severity: val?.highest_severity || "info",
              };
              return acc;
            }, {});

            setClaimsData((prev) => ({
              ...prev,
              [status]: {
                ...prev[status],
                data: prev[status].data.map((c) => {
                  const key = c?.code || c?.checkin_id || c?.id;
                  const fm = key ? flagsMap[key] : undefined;
                  return fm ? { ...c, ...fm } : c;
                }),
              },
            }));
          })
          .catch((e) => {
            console.warn("Flags enrichment failed:", e);
          });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch authorization requests",
        placement: "topRight",
      });
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentChange = useCallback(
    (status) => {
      setFilterKeyword("");
      setSearchText("");
      localStorage.setItem("paActiveSegment", status);
      setActiveSegment(status);
      const params = new URLSearchParams(location.search);
      params.set("s", status);
      navigate(`?${params.toString()}`, { replace: true });
    },
    [navigate, location.search]
  );

  const handlePageChange = (page) => {
    fetchSegmentData(activeSegment, page, filterKeyword);
  };

  const runSearch = useCallback(() => {
    const value = searchText.trim();
    setFilterKeyword(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    fetchSegmentData(activeSegment, 1, value);
  }, [searchText, activeSegment, searchTimeout]);

  const handleSearchInput = (value) => {
    setSearchText(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(
      setTimeout(() => {
        const trimmed = value.trim();
        setFilterKeyword(trimmed);
        fetchSegmentData(activeSegment, 1, trimmed);
      }, 500)
    );
  };

  const refreshQueue = useCallback(() => {
    const page = claimsData[activeSegment]?.pagination?.current || 1;
    fetchSegmentData(activeSegment, page, filterKeyword);
  }, [activeSegment, filterKeyword, claimsData, queueFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const openClaim = useCallback(
    (claim) => {
      navigate(`/preauth-action?checkin=${claim.code}&status=${activeSegment}`);
    },
    [navigate, activeSegment]
  );

  const handleFacilitySearch = async (q) => {
    if (!q || String(q).length < 3) return [];
    try {
      const resp = await lib.unifiedSearch({
        resource: "facilities",
        search: q,
        page: 1,
        pageSize: 50,
      });
      const body = resp?.data;
      if (body && Number(body?.error) === 0) {
        return (body.data || []).map((loc) => ({
          label: loc.name || loc.facility_name,
          value: String(loc.id || loc.facility_id),
        }));
      }
    } catch {
      /* ignore */
    }
    return [];
  };

  const dateRangeValue = queueFilters.fromDate && queueFilters.toDate
    ? [dayjs(queueFilters.fromDate), dayjs(queueFilters.toDate)]
    : queueFilters.fromDate
      ? [dayjs(queueFilters.fromDate), null]
      : null;

  const currentData = claimsData[activeSegment];
  const currentStatusConfig = STATUS_CONFIG[activeSegment];
  const claims = currentData?.data || [];
  const pagination = currentData?.pagination || { current: 1, pageSize: 20, total: 0 };

  const isLoadingState = loading && !claims.length;

  return (
    <div className="preauth-queue-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Pre-authorization</h1>
            <p className="dash-masthead__meta">Review and action pending authorization requests.</p>
          </div>
          <div className="dash-masthead__actions">
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={refreshQueue}
              loading={loading}
              className="dash-masthead__refresh"
            >
              Refresh
            </Button>
          </div>
        </header>

        <DashboardSection
          id="preauth-queue"
          icon={<FileProtectOutlined />}
          title="Authorization queue"
          subtitle="Select a status, search, then open a request to review"
          collapsible={false}
          variant="content"
        >
          <div className="preauth-queue-controls">
            <div className="preauth-status-filter" role="tablist" aria-label="Authorization status">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                const total = claimsData[key]?.pagination?.total;
                const isActive = activeSegment === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`preauth-status-pill${isActive ? " preauth-status-pill--active" : ""}`}
                    style={{ "--pill-color": config.color }}
                    onClick={() => handleSegmentChange(key)}
                  >
                    <span className="preauth-status-pill__dot" aria-hidden />
                    <span>{config.text}</span>
                    {total > 0 ? (
                      <span className="preauth-status-pill__count">{total}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            <div className="preauth-queue-controls__search">
              <Input
                placeholder={`Search ${currentStatusConfig.text.toLowerCase()}…`}
                value={searchText}
                onChange={(e) => handleSearchInput(e.target.value)}
                onPressEnter={runSearch}
                allowClear
                disabled={loading}
                size="middle"
              />
              <Button
                type="primary"
                size="middle"
                icon={<SearchOutlined />}
                onClick={runSearch}
                loading={loading}
              >
                Search
              </Button>
            </div>
          </div>

          <div className="preauth-queue-filters">
            <div className="preauth-queue-filters__field preauth-queue-filters__field--grow">
              <span className="preauth-queue-filters__label">Facility</span>
              <DebounceSelect
                allowClear
                showSearch
                labelInValue
                placeholder="Filter by facility"
                value={facilityOption}
                fetchOptions={handleFacilitySearch}
                onChange={(option) => {
                  setFacilityOption(option || null);
                  writeQueueParams({ facility_id: option?.value ? String(option.value) : "" });
                }}
                style={{ width: "100%" }}
              />
            </div>
            <div className="preauth-queue-filters__field">
              <span className="preauth-queue-filters__label">Dates</span>
              <DatePicker.RangePicker
                allowClear
                value={dateRangeValue}
                onChange={(vals) => {
                  writeQueueParams({
                    fromDate: vals?.[0] ? vals[0].format("YYYY-MM-DD") : "",
                    toDate: vals?.[1] ? vals[1].format("YYYY-MM-DD") : "",
                  });
                }}
                style={{ width: "100%" }}
              />
            </div>
            <div className="preauth-queue-filters__field">
              <span className="preauth-queue-filters__label">Type</span>
              <Select
                allowClear
                placeholder="All types"
                value={queueFilters.checkin_type_id || undefined}
                options={
                  checkinTypes.length
                    ? checkinTypes
                    : Object.entries(CHECKIN_TYPE_MAP)
                      .filter(([key]) => /^\d+$/.test(key))
                      .map(([value, label]) => ({ value, label }))
                }
                onChange={(value) => writeQueueParams({ checkin_type_id: value || "" })}
                style={{ width: "100%" }}
              />
            </div>
            <div className="preauth-queue-filters__field" style={{ flex: "0 0 auto", minWidth: "auto" }}>
              <span className="preauth-queue-filters__label">Flags</span>
              <Checkbox
                checked={!!queueFilters.flagged}
                onChange={(e) => writeQueueParams({ flagged: e.target.checked })}
              >
                Flagged only
              </Checkbox>
            </div>
          </div>

          <div className="preauth-queue-toolbar">
            <span className="preauth-queue-toolbar__count">
              {loading
                ? "Loading…"
                : `${currentStatusConfig.text} · ${claims.length} of ${pagination.total || claims.length}`}
            </span>
            {claims.length > 0 || loading ? (
              <Segmented
                className="preauth-view-toggle"
                size="small"
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: isMobileView ? "" : "Cards", value: "grid", icon: <AppstoreOutlined /> },
                  { label: isMobileView ? "" : "Table", value: "table", icon: <TableOutlined /> },
                ]}
              />
            ) : null}
          </div>

          {isLoadingState ? (
            viewMode === "table" ? (
              <TableSkeleton rows={8} columns={5} />
            ) : (
              <SearchResultsSkeleton count={6} />
            )
          ) : claims.length === 0 ? (
            <div className="preauth-empty">
              <FileProtectOutlined style={{ fontSize: 32, color: "#94a3b8" }} />
              <p className="preauth-empty__title">
                No {currentStatusConfig.text.toLowerCase()} authorizations found
              </p>
              <p className="preauth-empty__desc">
                {filterKeyword
                  ? `No results for "${filterKeyword}". Try a different search term.`
                  : `${currentStatusConfig.text} authorizations will appear here when available.`}
              </p>
            </div>
          ) : (
            <>
              {viewMode === "table" ? (
                <PAQueueTable
                  data={claims}
                  onOpenClaim={openClaim}
                  sortBy={queueFilters.sort_by}
                  sortDir={queueFilters.sort_dir}
                  from={queueFilters.fromDate}
                  to={queueFilters.toDate}
                  itemsMin={queueFilters.items_min === "" ? undefined : Number(queueFilters.items_min)}
                  itemsMax={queueFilters.items_max === "" ? undefined : Number(queueFilters.items_max)}
                  requestedMin={queueFilters.requested_min === "" ? undefined : Number(queueFilters.requested_min)}
                  requestedMax={queueFilters.requested_max === "" ? undefined : Number(queueFilters.requested_max)}
                  approvedMin={queueFilters.approved_min === "" ? undefined : Number(queueFilters.approved_min)}
                  approvedMax={queueFilters.approved_max === "" ? undefined : Number(queueFilters.approved_max)}
                  onDateRangeChange={(dates) => {
                    writeQueueParams({
                      fromDate: dates?.[0] ? dates[0].format("YYYY-MM-DD") : "",
                      toDate: dates?.[1] ? dates[1].format("YYYY-MM-DD") : "",
                    });
                  }}
                  onSortChange={(next) => writeQueueParams({
                    sort_by: next.sort_by || SORT_BY.CHECKIN_DATE,
                    sort_dir: next.sort_dir || SORT_DIR.DESC,
                    items_min: next.items_min,
                    items_max: next.items_max,
                    requested_min: next.requested_min,
                    requested_max: next.requested_max,
                    approved_min: next.approved_min,
                    approved_max: next.approved_max,
                  }, { resetPage: true })}
                />
              ) : (
                <div className="preauth-cards-grid">
                  {claims.map((claim) => (
                    <PAClaimListingCard
                      key={claim.code}
                      claim={claim}
                      onClick={() => openClaim(claim)}
                    />
                  ))}
                </div>
              )}

              {(pagination.total || 0) > pagination.pageSize ? (
                <div className="preauth-pagination">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} claims`}
                  />
                </div>
              ) : null}
            </>
          )}
        </DashboardSection>
      </div>
    </div>
  );
};

const PAHook = () => {
  return <PAListHook />;
};

export default PAHook;
